import type {EPPlateType, EPTemplateOption} from '../ep-template/ep-templates';

// Pure rules of the personalized-plate module (INST-PLATES D6, PRO-PLATES D5): the sizes and plate
// types it offers (derived from the templates a product links), which templates it lists for a
// choice, what a change does to the selection, the plate finish, the add-to-cart gate and the
// restore of a cart line's share link. No store access here, so every rule is unit-tested on its
// own (inst-plate-rules.test.ts).

/** The typed plate_type values: the collegiate name family and the profession licence family ("any" is neither). */
export type InstPlateType = Exclude<EPPlateType, 'any'>;

export type InstPlateFamily = 'name' | 'license';

export type InstPlateTypeOption = {value: InstPlateType; label: string};

/** One family's "Plate Type" buttons, in order, and the type active until the shopper picks one. */
export type InstPlateTypeSet = {types: readonly InstPlateTypeOption[]; defaultType: InstPlateType};

export type PlateSize = {width: number; height: number};

export type InstPlateSize = PlateSize & {key: string; label: string};

/** The module's plate sizes, in tile order; a product shows the ones its typed templates come in (instPlateSizesFor). */
export const INST_PLATE_SIZES: readonly InstPlateSize[] = [
    {key: '3x1', width: 3, height: 1, label: '3" x 1"'},
    {key: '3x2', width: 3, height: 2, label: '3" x 2"'},
    {key: '4.75x2.75', width: 4.75, height: 2.75, label: '4.75" x 2.75"'},
    {key: '6.25x1.75', width: 6.25, height: 1.75, label: '6.25" x 1.75"'},
];

/** The size the product page opens with: a blank 3" x 1" plate (QA 2026-10-28; the spec had 3" x 2"). Every typed set has one. */
export const INST_PLATE_DEFAULT_SIZE: InstPlateSize = INST_PLATE_SIZES[0];

/**
 * The two families of typed templates: the collegiate With Name / Without Name (With Name default) and the
 * profession With License / Without License (Without License default, the spec's; its doc lists With License
 * first). A product's family is that of its typed templates (plateTypeSetFor); "any" templates are listed under both.
 */
export const INST_PLATE_TYPE_SETS: Record<InstPlateFamily, InstPlateTypeSet> = {
    name: {
        types: [
            {value: 'with_name', label: 'With Your Name'},
            {value: 'without_name', label: 'Without Your Name'},
        ],
        defaultType: 'with_name',
    },
    license: {
        types: [
            {value: 'with_license', label: 'With License'},
            {value: 'without_license', label: 'Without License'},
        ],
        defaultType: 'without_license',
    },
};

/** Plate finishes (EngravedPlateID): the embossing colour decides, there is no finish picker. */
export const INST_PLATE_FINISH_GOLD = 37;
export const INST_PLATE_FINISH_SILVER = 39;

export const INST_PLATE_ATC_LABEL = 'Approve & Add to Cart';

/** Shown by the add-to-cart gate; the server (DF4 ProductATC) refuses an incomplete plate with the same text. */
export const INST_PLATE_GATE_MESSAGE = 'Please choose a plate template, complete its lines and accept the personalization terms before adding to cart.';

/** Where "Custom Personalization Terms" links: the personalization terms are part of the site's Terms & Conditions page (Jira WEB-1240). */
export const PERSONALIZATION_TERMS_URL = '/terms-and-conditions';

/** A product gets the module when any of its plate templates carries a settingsJSON plate_type. */
export const isInstPlateTemplateSet = (templates: ReadonlyArray<Pick<EPTemplateOption, 'plateType'>> | null | undefined): boolean => !!templates && templates.some(t => !!t.plateType);

export const sameSize = (a: PlateSize, b: PlateSize): boolean => Math.abs(a.width - b.width) < 0.001 && Math.abs(a.height - b.height) < 0.001;

/** The module size a plate size is, or null when it is none of the four. */
export const instPlateSizeFor = (width: number, height: number): InstPlateSize | null => INST_PLATE_SIZES.find(sz => sameSize(sz, {width, height})) ?? null;

/** "3" x 2"" for a module size, else the inches as they are. */
export const plateSizeLabel = (size: PlateSize): string => instPlateSizeFor(size.width, size.height)?.label ?? `${size.width}" x ${size.height}"`;

/** The plate as the module header and the proof name it, e.g. 3" x 1" Gold (no finish word for another EngravedPlateID). */
export const plateSummary = (size: PlateSize, epId: number): string => {
    const finish = epId === INST_PLATE_FINISH_SILVER ? 'Silver' : epId === INST_PLATE_FINISH_GOLD ? 'Gold' : '';
    return `${plateSizeLabel(size)}${finish ? ` ${finish}` : ''}`;
};

/**
 * The plate preview's largest width: 96 CSS px to the inch (a 3" plate 288px wide, the 6.25" one 600px), so the
 * preview and the proof show the four sizes in proportion wherever the box is wide enough; a narrower box caps a
 * plate at its own width (QA 2026-10-28: every size showed 340px wide).
 */
export const platePreviewMaxWidthPx = (widthIn: number): number => Math.round(widthIn * 96);

/** The type selector a template belongs to (any of the four); null for "any" (listed under both) and untyped templates. */
export const plateTypeOfTemplate = (template: Pick<EPTemplateOption, 'plateType'> | null | undefined): InstPlateType | null => (template?.plateType && template.plateType !== 'any' ? template.plateType : null);

/**
 * The family of a product's templates: the licence set when any template is typed with_license / without_license,
 * else the name set (so an all-"any" or empty set keeps the collegiate labels). A product mixing both families is
 * not expected.
 */
export const plateTypeSetFor = (templates: ReadonlyArray<Pick<EPTemplateOption, 'plateType'>> | null | undefined): InstPlateTypeSet => ((templates || []).some(t => INST_PLATE_TYPE_SETS.license.types.some(o => o.value === t.plateType)) ? INST_PLATE_TYPE_SETS.license : INST_PLATE_TYPE_SETS.name);

/**
 * The size tiles: the master list in its order, kept to the sizes with at least one typed template ("any" counts;
 * the collegiate and profession sets have all four); the whole list when no template is typed.
 */
export const instPlateSizesFor = (templates: ReadonlyArray<Pick<EPTemplateOption, 'plateType' | 'width' | 'height'>> | null | undefined): readonly InstPlateSize[] => {
    const typed = (templates || []).filter(t => !!t.plateType);
    const sizes = INST_PLATE_SIZES.filter(sz => typed.some(t => sameSize(sz, t)));
    return sizes.length > 0 ? sizes : INST_PLATE_SIZES;
};

/**
 * The templates the "Select a Template" dropdown lists: the typed templates of exactly this
 * size whose plate_type is the chosen one or "any", sorted by name (then id, so equal names
 * keep a stable order). Untyped templates are never listed.
 */
export const templatesFor = (templates: readonly EPTemplateOption[] | null | undefined, size: PlateSize, plateType: InstPlateType): EPTemplateOption[] =>
    (templates || []).filter(t => sameSize(t, size) && (t.plateType === plateType || t.plateType === 'any')).sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);

export type InstPlateSelection = {size: PlateSize; plateType: InstPlateType; templateId: number};

/**
 * After a size or type change: the list for the new choice, and the selected template if it
 * is still in it. A template that is not is cleared (templateId -1, cleared true); another
 * one is never substituted for it.
 */
export const selectionAfterChange = (templates: readonly EPTemplateOption[] | null | undefined, next: InstPlateSelection): {templateId: number; cleared: boolean; available: EPTemplateOption[]} => {
    const available = templatesFor(templates, next.size, next.plateType);
    if (!(next.templateId > 0)) {
        return {templateId: -1, cleared: false, available};
    }
    const kept = available.some(t => t.id === next.templateId);
    return {templateId: kept ? next.templateId : -1, cleared: !kept, available};
};

/** The plate finish for the selected insignia's colour: silver (pewter is stored as silver) -> 39, anything else -> 37. */
export const plateFinishFor = (insColor: string | null | undefined): number => ((insColor || '').trim().toLowerCase() === 'silver' ? INST_PLATE_FINISH_SILVER : INST_PLATE_FINISH_GOLD);

export type InstPlateGateState = {
    templateId: number;
    // s.epTemplateMissingLineTitles: the template's lines left blank or failing their pattern
    missingLineTitles: readonly string[];
    // the certification box
    approved: boolean;
};

/** The add-to-cart gate: the message while no template is chosen, a line is blank or invalid, or the box is unticked; null when the plate may go to the cart. */
export const gateMessage = (state: InstPlateGateState): string | null => (state.templateId > 0 && state.missingLineTitles.length === 0 && state.approved ? null : INST_PLATE_GATE_MESSAGE);

export type InstPlateAcknowledgment = {approved: boolean; confirmed: boolean};

/** Any change to the size, type, template or a line: the box is unticked and the proof must be confirmed again. */
export const resetAcknowledgment = <T extends InstPlateAcknowledgment>(state: T): T => ({...state, approved: false, confirmed: false});

/** The plate part of a share link (ProductShare): t = EPTemplateID, epw / eph = the plate size. */
export type InstPlateShare = {t?: unknown; epw?: unknown; eph?: unknown};

export type InstPlateRestore = {
    templateId: number;
    template: EPTemplateOption | null;
    size: PlateSize;
    plateType: InstPlateType;
    approved: false;
    confirmed: false;
};

const shareNumber = (value: unknown): number => (typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : NaN);

/**
 * A cart line's "Edit Frame" link: the saved template (t) when it is still one of the
 * product's typed templates, with its size and type ("any" keeps the family's default type);
 * else no template, at the link's plate size when it is one of the four (3" x 1" otherwise), under
 * the family's default type. The lines and EPContent are the caller's to keep; the box and the
 * confirmation always start cleared.
 */
export const restoreFromShare = (share: InstPlateShare | null | undefined, templates: readonly EPTemplateOption[] | null | undefined): InstPlateRestore => {
    const id = Math.trunc(shareNumber(share?.t));
    const template = id > 0 ? ((templates || []).find(t => t.id === id && !!t.plateType) ?? null) : null;
    const defaultType = plateTypeSetFor(templates).defaultType;

    if (template) {
        return {
            templateId: template.id,
            template,
            size: {width: template.width, height: template.height},
            plateType: plateTypeOfTemplate(template) ?? defaultType,
            approved: false,
            confirmed: false,
        };
    }

    const shared = instPlateSizeFor(shareNumber(share?.epw), shareNumber(share?.eph));
    const size = shared ?? INST_PLATE_DEFAULT_SIZE;
    return {templateId: -1, template: null, size: {width: size.width, height: size.height}, plateType: defaultType, approved: false, confirmed: false};
};
